import { useQuery } from '@tanstack/react-query';

import { medicalStaffApi } from '@/features/medical-staff/api/medical-staff.adapter';
import type { MedicalStaff } from '@/features/medical-staff/types/medical-staff.types';

// Candidates for the "Add physician" picker in the registration modal —
// every Registered medical-staff row. Per PRD §3.7, only Registered staff
// can be assigned. Withdrawn rows are excluded by the status filter; the
// caller's row is filtered out client-side by the picker once it knows the
// caller's email.
//
// Reuses the existing Medical Staff API (PRD 03) — no new BE endpoint needed.
// We request a large perPage so the picker has the full list cached and can
// filter client-side without re-fetching on every keystroke.
const PICKER_QUERY_KEY = ['patients', 'staff-picker'] as const;
const STALE_MS = 5 * 60 * 1000;
const PICKER_PAGE_SIZE = 500;

export interface StaffPickerCandidate {
  id: number;
  name: string;
  email: string;
  department: string;
  jobRole: string;
}

export function useStaffPickerQuery() {
  return useQuery({
    queryKey: PICKER_QUERY_KEY,
    queryFn: async (): Promise<StaffPickerCandidate[]> => {
      const result = await medicalStaffApi.listMedicalStaff({
        status: 'ACTIVE',
        page: 1,
        perPage: PICKER_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      });
      return result.items.map((s: MedicalStaff) => ({
        id: s.id,
        name: s.name,
        email: s.email,
        department: s.department,
        jobRole: s.jobRole,
      }));
    },
    staleTime: STALE_MS,
  });
}

// (SHCT-537) `useResolveSelfStaff` was removed together with the caller's
// auto-assignment as 주담당의 — [ADM.030] requires the operator to pick every
// physician from the search modal, so the caller's own row is no longer special.
